/**
 * <cart-rewards> — headless free-gift reconciler.
 *
 * Keeps the cart's `_free_gift` lines in sync with the reward tiers configured
 * in the merchant-owned `zena.cart_rewards` shop metafield (delivered via the
 * JSON island rendered by snippets/custom-cart-rewards-boot.liquid). Adds the
 * gift for every earned tier, removes gifts for tiers the shopper no longer
 * qualifies for, and resets any gift line back to quantity 1. Purely
 * best-effort UX: nothing here prices a gift or decides whether it may check
 * out. In the app repo a Cart Transform prices every one-time `_free_gift`
 * line at $0 and a Validation reads this same metafield to block an unearned
 * one at checkout; a gift tier must not be configured until both are
 * registered — see .wiki/Cart-Rewards.md.
 *
 * A gift tier is earned one of two ways, and the test must stay identical
 * with snippets/custom-cart-rewards-bar.liquid:
 *   - `requiresSellingPlanIds` present: a NON-GIFT cart line is on one of those
 *     selling plans. Any subtotal; thresholdCents is ignored.
 *   - otherwise: qualifying subtotal >= thresholdCents, where qualifying =
 *     cart.total_price − Σ gift lines' final_line_price. The subtraction used
 *     to be near-moot because earned gifts were priced $0; it carries real
 *     weight whenever nothing is zeroing them.
 *
 * Network shape: when the triggering `cartUpdate` event carries a full cart
 * payload it is used directly (no extra GET), and every mutation requests the
 * re-rendered cart sections in the same call (Section Rendering API), which
 * this element applies itself. The follow-up publish uses source
 * 'cart-items' — Dawn's own "the cart UI is already fresh for this change"
 * signal (see CartItems.updateQuantity) — so no subscriber refetches what was
 * just applied.
 *
 * Reconciliation is coalesced: a pass that is interrupted by another cart
 * write runs one trailing pass against the fresh cart. The element only
 * refreshes the UI when a mutation actually succeeded, so a failed add
 * (e.g. gift out of stock) cannot cause a publish/reconcile loop — it is
 * retried on the next natural cart write instead. Every failed mutation is
 * reported through reportGiftFailure with the reason from the API body, since
 * the shopper sees nothing: the rail marks a tier earned off the subtotal
 * alone, whether or not the gift line actually landed.
 */
if (!customElements.get('cart-rewards')) {
  customElements.define(
    'cart-rewards',
    class CartRewards extends HTMLElement {
      // Bind in the constructor: the theme editor detaches and re-attaches
      // elements, and connectedCallback can run more than once.
      constructor() {
        super();
        this.onCartUpdate = this.onCartUpdate.bind(this);
        this.onRailClick = this.onRailClick.bind(this);
        this.isReconciling = false;
        this.reconcileRequested = false;
        this.barNeedsRefresh = false;
      }

      connectedCallback() {
        this.giftTiers = this.parseGiftTiers();
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
        }
        this.setupBarAnimations();
        // Delegated on the document: both bars (drawer and page) are replaced
        // wholesale by section renders, so a listener on either would be lost.
        document.addEventListener('click', this.onRailClick);
        // Reconcile once on load (the cart may have been mutated elsewhere,
        // e.g. another device or a closed tab) — skipped when the cart is empty.
        if (this.giftTiers.length && this.dataset.cartItemCount !== '0') this.reconcile();
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onRailClick);
        this.cartUpdateUnsubscriber?.();
        this.cartUpdateUnsubscriber = undefined;
        this.barObserver?.disconnect();
        this.barObserver = undefined;
      }

      // -----------------------------------------------------------------
      // Rail animations
      //
      // The rail is server-rendered and swapped wholesale (innerHTML /
      // replaceWith) on every cart change, so a fresh node carries no
      // previous state and CSS transitions never fire on their own. One
      // observer watches the persistent containers for rail replacements and
      // hands the old state to the new node.
      //
      // Two different handovers, because the meter is per-SEGMENT (last
      // threshold crossed -> next one) rather than per-ladder:
      //
      //   - No tier flipped: same segment, so seed the fresh fill with the
      //     previous width and let it transition to the new one.
      //   - A tier flipped: the meter now measures a DIFFERENT segment, and
      //     seeding it with the old width would animate backwards (95% of
      //     $55->$80 becoming 0% of $80->$100). Run it up from zero instead
      //     and pop the node that was just claimed.
      //
      // The state snapshot also carries GEOMETRY, because unlocking a tier
      // relays the whole rail — the hero shrinks to a chip and the next chip
      // expands into the hero — and a wholesale swap would cut straight to
      // the new layout. morphRail() replays that as a FLIP.
      // -----------------------------------------------------------------

      barSlot(slot) {
        return slot === 'drawer'
          ? document.querySelector('#CartDrawer .cart-rewards')
          : document.querySelector('cart-items .cart-rewards');
      }

      readBarState(bar) {
        if (!bar) return null;
        return {
          node: bar,
          complete: !!bar.querySelector('.cart-rewards__status--complete'),
          pct: parseFloat(bar.querySelector('.cart-rewards__fill')?.style.width) || 0,
          // Measured while this render is live in the DOM; by the time it is
          // the `previous` state its node is detached and unmeasurable.
          trayHeight: bar.getBoundingClientRect().height,
          railWidth: bar.querySelector('.cart-rewards__rail')?.getBoundingClientRect().width || 0,
          nodes: [...bar.querySelectorAll('.cart-rewards__node')].map((node) => ({
            earned: node.classList.contains('cart-rewards__node--earned'),
            width: node.getBoundingClientRect().width,
            // Earned nodes are squares that opt out of the row's stretch, so
            // claiming a tier changes a node's height as well as its width.
            height: node.getBoundingClientRect().height,
            art: node.querySelector('.cart-rewards__node-art')?.getBoundingClientRect().width || 0,
            background: getComputedStyle(node).backgroundColor,
          })),
        };
      }

      /**
       * FLIP the rail from the previous layout to the one just rendered:
       * every node (and its art box) animates from the width it had to the
       * width it now has, the tray animates its height, and the tint
       * cross-fades. Without this, claiming a tier snaps a 24rem hero down to
       * a 6.4rem chip and blows the next chip up to hero size in one frame.
       *
       * Runs from the MutationObserver callback — a microtask after the swap
       * but before paint — so the new layout is measured but never shown.
       *
       * Widths are pinned inline (`flex: 0 0 auto` + an explicit width, with
       * min/max cleared) for the duration: the hero's own `min-width: 16rem`
       * would otherwise refuse to animate down through it. Both layouts fill
       * the same rail, so interpolating every node at once cannot overflow.
       */
      morphRail(bar, previous, next) {
        // A zero or changed rail width means the previous snapshot describes a
        // different viewport (or was taken before first paint) — its pixel
        // values would be nonsense to animate from.
        if (!previous.railWidth || previous.railWidth !== next.railWidth) return;
        if (previous.nodes.length !== next.nodes.length) return;

        const nodes = [...bar.querySelectorAll('.cart-rewards__node')];
        if (nodes.length !== next.nodes.length) return;

        const moved = next.nodes.some(
          (n, i) =>
            Math.abs(n.width - previous.nodes[i].width) > 1 || Math.abs(n.height - previous.nodes[i].height) > 1,
        );
        const grew = Math.abs(next.trayHeight - previous.trayHeight) > 1;
        if (!moved && !grew) return;

        const timing = { duration: 460, easing: 'cubic-bezier(0.2, 0, 0, 1)' };

        if (grew) {
          // Deliberately unclipped: the tray is a fixed row above the drawer's
          // scroll area and the delta is small, so letting the taller content
          // sit proud for a few frames reads better than an overflow window
          // shearing the check stamps off the nodes.
          bar.animate([{ height: `${previous.trayHeight}px` }, { height: `${next.trayHeight}px` }], timing);
        }

        if (!moved) return;

        nodes.forEach((node, index) => {
          const from = previous.nodes[index];
          const to = next.nodes[index];
          const restore = node.getAttribute('style');

          node.style.flex = '0 0 auto';
          node.style.minWidth = '0';
          node.style.maxWidth = 'none';
          // Pinned to the TARGET, not the start: the keyframes below supply the
          // start. These animations do not fill forwards, so between the last
          // animated frame and the microtask that unpins, the node paints from
          // this inline value — the old width here would flash the previous
          // layout for a frame at the very end of every morph.
          node.style.width = `${to.width}px`;
          // Same pin-to-target reasoning as the width, plus: an explicit height
          // is what lets a node that is about to become an earned square stop
          // being stretched by the row mid-flight.
          node.style.height = `${to.height}px`;
          node.style.alignSelf = 'center';

          const animation = node.animate(
            [
              { width: `${from.width}px`, height: `${from.height}px`, backgroundColor: from.background },
              { width: `${to.width}px`, height: `${to.height}px`, backgroundColor: to.background },
            ],
            timing,
          );
          // Unpin on cancel as well as on finish. A cancelled animation leaves
          // the node pinned to its OLD width forever, and the rejection path
          // is the one a swallowed catch would hide.
          const unpin = () => {
            if (restore == null) node.removeAttribute('style');
            else node.setAttribute('style', restore);
          };
          animation.finished.then(unpin, unpin);

          const art = node.querySelector('.cart-rewards__node-art');
          if (art && Math.abs(to.art - from.art) > 1) {
            art.animate(
              [
                { width: `${from.art}px`, height: `${from.art}px` },
                { width: `${to.art}px`, height: `${to.art}px` },
              ],
              timing,
            );
          }

          // The row under the label is a different element in each state —
          // meter, "Free" pill, threshold — so there is nothing to tween
          // between. Fade the replacement in over the resize instead.
          if (from.width !== to.width) {
            node
              .querySelectorAll(
                '.cart-rewards__meter, .cart-rewards__node-remaining, .cart-rewards__node-pill, .cart-rewards__node-threshold',
              )
              .forEach((el) => el.animate([{ opacity: 0 }, { opacity: 1 }], { ...timing, duration: 300, delay: 160 }));
          }
        });
      }

      /**
       * Keep the tier being chased in view. The rail only overflows when a
       * merchant configures more tiers than fit (see component-cart-rewards.css),
       * so this is usually a no-op — but when it does overflow, the node that
       * matters is the one the shopper must not have to go looking for.
       *
       * `scrollIntoView` on the element itself would also scroll the page /
       * drawer to reach it; setting scrollLeft confines the movement to the
       * rail.
       */
      /**
       * Which locked gift's switch card is showing, when the rail holds more
       * than one. The server renders every locked plan-gated tier twice — card
       * and chip, `data-rail-role` hero|chip, paired by `data-rail-tier` — with
       * the first tier's card visible. A tap on a chip brings its card forward
       * and turns the previous card back into a chip. Pure visibility: every
       * word, price and plan id was rendered by Liquid; this only moves the
       * `hidden` attribute. The pick lives for the session so the card the
       * shopper chose survives the re-render every cart write causes.
       */
      static PICK_KEY = 'zena-rewards-pick';

      readPick() {
        try {
          return sessionStorage.getItem(CartRewards.PICK_KEY);
        } catch {
          return null;
        }
      }

      storePick(tier) {
        try {
          sessionStorage.setItem(CartRewards.PICK_KEY, tier);
        } catch {
          /* private mode or blocked storage — the pick just doesn't persist */
        }
      }

      onRailClick(event) {
        const chip = event.target.closest('.cart-rewards__node[data-rail-role="chip"]');
        if (!chip || chip.hidden) return;
        const bar = chip.closest('.cart-rewards');
        if (!bar) return;
        event.preventDefault();
        this.storePick(chip.dataset.railTier);
        this.applyPick(bar, chip.dataset.railTier);
        this.centreOnHero(bar, true);
      }

      /** Show `tier`'s card and everyone else's chip; nothing if the bar has no such pair. */
      applyPick(bar, tier = this.readPick()) {
        if (!bar || !tier) return;
        const heroes = bar.querySelectorAll('.cart-rewards__node[data-rail-role="hero"]');
        if (![...heroes].some((node) => node.dataset.railTier === tier)) return;
        heroes.forEach((node) => {
          node.hidden = node.dataset.railTier !== tier;
        });
        bar.querySelectorAll('.cart-rewards__node[data-rail-role="chip"]').forEach((node) => {
          node.hidden = node.dataset.railTier === tier;
        });
      }

      centreOnHero(bar, smooth) {
        const rail = bar.querySelector('.cart-rewards__rail');
        const hero = rail?.querySelector(
          '.cart-rewards__node--next:not([hidden]), .cart-rewards__node--plan:not([hidden])',
        );
        if (!rail || !hero || rail.scrollWidth <= rail.clientWidth) return;
        rail.scrollTo({
          left: hero.offsetLeft - (rail.clientWidth - hero.offsetWidth) / 2,
          behavior: smooth && !this.reducedMotion.matches ? 'smooth' : 'auto',
        });
      }

      setupBarAnimations() {
        if (this.barObserver) return;
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.lastBarStates = new Map();
        for (const slot of ['drawer', 'page']) {
          const bar = this.barSlot(slot);
          if (bar) this.applyPick(bar);
          this.lastBarStates.set(slot, this.readBarState(bar));
          if (bar) this.centreOnHero(bar, false);
        }
        // One body-level observer with dynamic lookups: the drawer/page
        // containers themselves can be replaced wholesale (theme editor
        // section re-renders, dev hot reload), which would strand an
        // observer bound to them. The callback is cheap — two selector
        // lookups and node-identity checks per mutation batch.
        this.barObserver = new MutationObserver(() => this.onBarMaybeReplaced());
        this.barObserver.observe(document.body, { childList: true, subtree: true });
      }

      onBarMaybeReplaced() {
        for (const slot of ['drawer', 'page']) {
          const bar = this.barSlot(slot);
          const previous = this.lastBarStates.get(slot);
          if (!bar) {
            if (previous) this.lastBarStates.set(slot, null);
            continue;
          }
          // Same node — a mutation inside it (e.g. our own animation
          // classes), not a replacement. Nothing to hand over.
          if (previous && bar === previous.node) continue;

          // A fresh render shows the server's default card; put the shopper's
          // pick back before anything is measured or animated.
          this.applyPick(bar);
          const next = this.readBarState(bar);
          this.lastBarStates.set(slot, next);
          if (!previous) {
            this.centreOnHero(bar, false);
            continue;
          }
          if (this.reducedMotion.matches) continue;

          // Index-aligned because both renders walk the same threshold-sorted
          // tier list; a node present in one render and not the other (the
          // merchant edited the metafield mid-session) compares as undefined
          // and is skipped rather than mis-attributed.
          let claimed = 0;
          bar.querySelectorAll('.cart-rewards__node').forEach((node, index) => {
            if (previous.nodes[index]?.earned === false && next.nodes[index]?.earned === true) {
              node.classList.add('cart-rewards__node--changed');
              claimed += 1;
            }
          });

          // Before the fill is seeded: morphRail pins node widths inline, and
          // the meter's width is a percentage of the node it sits in.
          this.morphRail(bar, previous, next);

          const fill = bar.querySelector('.cart-rewards__fill');
          if (fill) {
            // A claim moved the meter onto the next segment, so there is no
            // continuous width to hand over — start it at zero. Otherwise
            // continue from where the previous render left off.
            const from = claimed ? 0 : previous.pct;
            if (Math.round(from) !== Math.round(next.pct)) {
              fill.style.transition = 'none';
              fill.style.width = `${from}%`;
              void fill.offsetWidth;
              fill.style.transition = '';
              fill.style.width = `${next.pct}%`;
            }
          }

          this.centreOnHero(bar, true);
        }
      }

      /**
       * Strict, fail-closed parse of the config island: discards tiers with a
       * non-finite/negative thresholdCents, a variantId that is neither number
       * nor null, or a `requiresSellingPlanIds` that is present but not a
       * non-empty array of positive numbers — a plan-gated tier whose gate is
       * unreadable must not fall through to the spend route, which would hand
       * the gift to every cart. Keeps only tiers that grant a gift line
       * (variantId != null). An absent or malformed island yields [] — the
       * element then never mutates the cart.
       */
      parseGiftTiers() {
        let raw = null;
        try {
          raw = JSON.parse(this.querySelector('[data-cart-rewards-config]')?.textContent ?? 'null');
        } catch {
          return [];
        }
        if (raw == null || typeof raw !== 'object' || !Array.isArray(raw.tiers)) return [];

        const tiers = [];
        for (const tier of raw.tiers) {
          if (tier == null || typeof tier !== 'object') continue;
          if (
            typeof tier.thresholdCents !== 'number' ||
            !Number.isFinite(tier.thresholdCents) ||
            tier.thresholdCents < 0
          )
            continue;
          if (typeof tier.variantId !== 'number') continue;
          let requiresSellingPlanIds = null;
          if (tier.requiresSellingPlanIds != null) {
            if (!Array.isArray(tier.requiresSellingPlanIds)) continue;
            requiresSellingPlanIds = tier.requiresSellingPlanIds.filter(
              (id) => typeof id === 'number' && Number.isFinite(id) && id > 0,
            );
            if (!requiresSellingPlanIds.length) continue;
          }
          // Product scope of a plan-gated tier (requiresProductIds allow-list,
          // excludesProductIds deny-list). Same discipline as the plan list: a
          // key that is present but unreadable discards the tier rather than
          // widening it to every product.
          const idList = (value) => {
            if (value == null) return null;
            if (!Array.isArray(value)) return undefined;
            const ids = value.filter((id) => typeof id === 'number' && Number.isFinite(id) && id > 0);
            return ids.length ? ids : undefined;
          };
          const requiresProductIds = idList(tier.requiresProductIds);
          if (requiresProductIds === undefined) continue;
          const excludesProductIds = idList(tier.excludesProductIds);
          if (excludesProductIds === undefined) continue;
          tiers.push({
            thresholdCents: tier.thresholdCents,
            variantId: tier.variantId,
            requiresSellingPlanIds,
            requiresProductIds,
            excludesProductIds,
            // Diagnostics only — reportGiftFailure names the tier a failed
            // mutation belongs to. Never rendered: the rail reads its own
            // label straight from the metafield, in Liquid.
            label: typeof tier.label === 'string' ? tier.label : '',
          });
        }
        return tiers;
      }

      /**
       * Report a cart mutation that did not land, with the reason Shopify gave.
       *
       * The AJAX Cart API puts its diagnosis in the response BODY
       * (`{ message, description }` — "The product 'X' is sold out.") and
       * returns a bare 422 for every rejection, so logging the status alone
       * makes a sold-out gift indistinguishable from a deleted variant, an
       * unpublished one, or a typo'd id in the metafield. Read the body first.
       *
       * console.error rather than warn: a gift that will not add is a config
       * fault someone has to go and fix — the tier stays configured, the rail
       * has already told the shopper they earned it, and no retry resolves it.
       * That silence is what hid a sold-out gift on the live store.
       *
       * Thresholds print in raw cents, matching the `thresholdCents` key in
       * `zena.cart_rewards`, so the message greps straight against the config.
       */
      async reportGiftFailure(action, variantId, response, error) {
        const tier = this.giftTiers.find((candidate) => candidate.variantId === variantId);
        const where = tier
          ? `tier "${tier.label || '(unlabelled)'}" (thresholdCents ${tier.thresholdCents}, variant ${variantId})`
          : `variant ${variantId}`;

        if (error) {
          console.error(`[cart-rewards] could not ${action} gift — ${where}:`, error);
          return;
        }

        let reason = '';
        try {
          // clone(), so the text fallback still has an unconsumed body when the
          // response is not JSON — a proxy or CDN error page, say.
          const body = await response.clone().json();
          reason = body?.description || body?.message || '';
        } catch {
          reason = await response.text().then(
            (text) => text.slice(0, 200),
            () => '',
          );
        }
        console.error(
          `[cart-rewards] could not ${action} gift — ${where}: ${response.status}${reason ? ` ${reason}` : ''}`,
        );
      }

      onCartUpdate(event) {
        // Most publishers (cart-items, quick-add, external-refresh) include
        // the full post-mutation cart — use it and skip the /cart.js GET.
        // product-form's payload is only the added line (no total_price), so
        // it fails this check and the pass fetches instead.
        const cartData = event?.cartData;
        const seed =
          cartData != null && Array.isArray(cartData.items) && typeof cartData.total_price === 'number'
            ? cartData
            : null;
        return this.reconcile(seed, event?.source);
      }

      isGiftLine(item) {
        return item.properties != null && '_free_gift' in item.properties;
      }

      /**
       * Re-render just the drawer's rewards bar. The bar lives OUTSIDE
       * <cart-drawer-items> (fixed layout: header / bar / scrollable items /
       * footer), so it is covered by quantity changes (.drawer__inner swap),
       * add-to-cart (#CartDrawer swap) and this element's own section apply —
       * but NOT by CartItems.onCartUpdate, which only replaces
       * cart-drawer-items + the footer for foreign publishers (quick-add,
       * external-refresh). This fills that one gap.
       */
      async refreshBarOnly() {
        if (!document.getElementById('CartDrawer')) return;
        try {
          const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
          if (!response.ok) return;
          const html = await response.text();
          const fresh = new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector('.drawer__inner > .cart-rewards');
          const current = document.querySelector('#CartDrawer .drawer__inner > .cart-rewards');
          if (fresh && current) current.replaceWith(fresh);
          else if (fresh && !current) document.querySelector('#CartDrawer .drawer__header')?.after(fresh);
          else if (!fresh && current) current.remove();
        } catch (error) {
          console.warn('[cart-rewards] bar refresh failed:', error);
        }
      }

      /** Announce a gift change on Dawn's cart status live region. */
      announce(text) {
        if (!text) return;
        const region =
          document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
        if (!region) return;
        region.setAttribute('aria-hidden', 'false');
        region.textContent = text;
        setTimeout(() => region.setAttribute('aria-hidden', 'true'), 1000);
      }

      async reconcile(seedCart = null, source = null) {
        if (!this.giftTiers.length) return;

        // Publishers whose own render path already refreshed the bar:
        // 'cart-items' swaps .drawer__inner, 'product-form' swaps #CartDrawer.
        // Anything else (quick-add, external-refresh) only replaces
        // cart-drawer-items + footer, leaving the bar stale when no gift
        // mutation follows — refresh it explicitly in that case.
        // Accumulated on the ELEMENT, never in a local. A foreign cart write
        // that lands while a pass is in flight returns at the coalescing check
        // below, and a local would carry its refresh obligation out with it —
        // leaving the rail showing the meter for a subtotal two writes ago
        // whenever the trailing pass then finds no gift work to do.
        if (source != null && source !== 'cart-items' && source !== 'product-form') {
          this.barNeedsRefresh = true;
        }

        // A pass is in flight — request a trailing pass so the gift set is
        // always reconciled against the latest cart, then return. The seed is
        // dropped on purpose: the trailing pass re-fetches, which is fresher.
        if (this.isReconciling) {
          this.reconcileRequested = true;
          return;
        }

        this.isReconciling = true;
        // Balanced in the finally, not per-iteration: a trailing pass can take
        // the flag a second time, and an error thrown anywhere in the loop
        // would otherwise leave the whole cart dimmed for good.
        let busyHeld = 0;
        try {
          do {
            // Clear before any await so a concurrent caller triggers exactly
            // one additional pass.
            this.reconcileRequested = false;

            const cart =
              seedCart ??
              (await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
                .then((response) => (response.ok ? response.json() : null))
                .catch(() => null));
            seedCart = null; // Only valid for the pass its event triggered.
            if (!cart || !Array.isArray(cart.items)) break;

            const giftTotal = cart.items.reduce(
              (sum, item) => (this.isGiftLine(item) ? sum + item.final_line_price : sum),
              0,
            );
            const qualifying = Math.max(0, cart.total_price - giftTotal);

            // The cart's NON-gift subscription lines as (plan, product) pairs — a
            // gift line can never qualify itself, however it was added. A
            // plan-gated tier may be scoped to products (requiresProductIds /
            // excludesProductIds), so the plan alone no longer decides: a line
            // earns a tier when its plan is required AND its product passes.
            // Same test as the rail and the cart-validation Function.
            const planLines = [];
            for (const item of cart.items) {
              if (this.isGiftLine(item)) continue;
              const planId = item.selling_plan_allocation?.selling_plan?.id;
              if (typeof planId === 'number') planLines.push({ planId, productId: item.product_id });
            }
            const lineEarns = (tier, line) =>
              tier.requiresSellingPlanIds.includes(line.planId) &&
              (!tier.requiresProductIds || tier.requiresProductIds.includes(line.productId)) &&
              (!tier.excludesProductIds || !tier.excludesProductIds.includes(line.productId));

            const desired = new Set();
            for (const tier of this.giftTiers) {
              if (tier.requiresSellingPlanIds) {
                if (planLines.some((line) => lineEarns(tier, line))) desired.add(tier.variantId);
              } else if (qualifying >= tier.thresholdCents) {
                desired.add(tier.variantId);
              }
            }

            const giftLines = cart.items.filter((item) => this.isGiftLine(item));
            const currentIds = new Set(giftLines.map((item) => item.variant_id));
            const toAdd = [...desired].filter((id) => !currentIds.has(id));
            const toRemove = giftLines.filter((item) => !desired.has(item.variant_id));
            // A gift is always exactly one unit. Concurrent reconcilers (two
            // open tabs racing on the same cart) can merge duplicate adds into
            // one line at quantity 2 — and neither this element nor the
            // checkout validation checks gift quantity, so reset it here.
            const toNormalize = giftLines.filter((item) => desired.has(item.variant_id) && item.quantity !== 1);

            if (toAdd.length === 0 && toRemove.length === 0 && toNormalize.length === 0) {
              if (this.barNeedsRefresh) {
                // Claimed before the await, so a write arriving during the
                // refresh sets the flag again rather than being swallowed.
                this.barNeedsRefresh = false;
                await this.refreshBarOnly();
              }
              continue;
            }
            // Mutations below re-render .drawer__inner (bar included).
            this.barNeedsRefresh = false;

            // Past this point the cart IS being mutated, so the prices, totals
            // and rail on screen are stale — say so until the swap lands. The
            // early-exit above deliberately sits outside it: a pass that finds
            // nothing to do must not flash the whole cart as pending.
            setCartBusy(true);
            busyHeld += 1;

            // Cache before mutating: dataset.id lookups need the elements to
            // still exist (same reason CartItems.updateQuantity caches).
            const sectionsToRender = cartSectionsToRender();
            const sectionIds = sectionsToRender.map((entry) => entry.section);
            const sectionParams = { sections: sectionIds, sections_url: window.location.pathname };

            // Every mutation requests re-rendered sections in the same call;
            // the last successful response's HTML is applied below — no
            // separate refresh round trip.
            let lastResponse = null;
            let addedCount = 0;
            let removedCount = 0;

            for (const item of toRemove) {
              try {
                const response = await fetch(routes.cart_change_url, {
                  ...fetchConfig(),
                  body: JSON.stringify({ id: item.key, quantity: 0, ...sectionParams }),
                });
                if (response.ok) {
                  lastResponse = await response.json();
                  removedCount += 1;
                } else {
                  await this.reportGiftFailure('remove', item.variant_id, response);
                }
              } catch (error) {
                await this.reportGiftFailure('remove', item.variant_id, null, error);
              }
            }

            for (const item of toNormalize) {
              try {
                const response = await fetch(routes.cart_change_url, {
                  ...fetchConfig(),
                  body: JSON.stringify({ id: item.key, quantity: 1, ...sectionParams }),
                });
                if (response.ok) lastResponse = await response.json();
                else await this.reportGiftFailure('normalize', item.variant_id, response);
              } catch (error) {
                await this.reportGiftFailure('normalize', item.variant_id, null, error);
              }
            }

            // One at a time so an unavailable gift (e.g. out of stock) fails
            // in isolation — /cart/add.js is all-or-nothing per request.
            for (const id of toAdd) {
              try {
                const response = await fetch(routes.cart_add_url, {
                  ...fetchConfig(),
                  body: JSON.stringify({
                    items: [{ id, quantity: 1, properties: { _free_gift: 'true' } }],
                    ...sectionParams,
                  }),
                });
                if (response.ok) {
                  lastResponse = await response.json();
                  addedCount += 1;
                } else {
                  await this.reportGiftFailure('add', id, response);
                }
              } catch (error) {
                await this.reportGiftFailure('add', id, null, error);
              }
            }

            // Refresh the UI only when something actually changed — this is
            // what guarantees the publish/reconcile cycle converges.
            if (lastResponse != null) {
              if (lastResponse.sections) {
                renderCartSections(lastResponse.sections, sectionsToRender);

                // Empty-cart classes are toggled by Dawn's components, not by
                // section HTML — mirror them when the payload knows the count
                // (change.js does; a gift removal can empty the cart).
                if (typeof lastResponse.item_count === 'number') {
                  const isEmpty = lastResponse.item_count === 0;
                  document.querySelector('cart-drawer')?.classList.toggle('is-empty', isEmpty);
                  document.querySelector('cart-items')?.classList.toggle('is-empty', isEmpty);
                  document.getElementById('main-cart-footer')?.classList.toggle('is-empty', isEmpty);
                }
              } else if (typeof refreshDawnCartUI === 'function') {
                // Defensive fallback — sections missing from the response.
                try {
                  await refreshDawnCartUI();
                } catch (error) {
                  console.warn('[cart-rewards] cart refresh failed:', error);
                }
              }

              // Source 'cart-items' is Dawn's "this change's cart UI is
              // already rendered" signal — CartItems skips its refetch (see
              // cart.js) exactly as it does for its own quantity updates.
              // cartData always carries an items array: quick-add-bulk reads
              // event.cartData.items unconditionally.
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: lastResponse });
              }

              if (addedCount > 0) this.announce(this.dataset.textGiftAdded);
              else if (removedCount > 0) this.announce(this.dataset.textGiftRemoved);
            }
          } while (this.reconcileRequested);
        } finally {
          this.isReconciling = false;
          while (busyHeld > 0) {
            busyHeld -= 1;
            setCartBusy(false);
          }
        }
      }
    },
  );
}
